import hasWhitespacesOnly from './common/hasWhitespacesOnly';
import incorrectDataType from './common/incorrectDataType';
import isEmpty from './common/isEmpty';
import isNull from './common/isNull';
import isUndefined from './common/isUndefined';

// Utility function to check for leading, trailing, or consecutive spaces
const hasInvalidSpaces = (address) => {
  return address.startsWith(" ") || address.endsWith(" ") || address.match(/  /);
};

export const isValidAddress = (address, minLength = 7, maxLength = 74) => {
  // Check for null, undefined, incorrect data type, whitespaces, and empty address
  if (
    isNull(address) ||
    isUndefined(address) ||
    incorrectDataType(address, 'string') ||
    hasWhitespacesOnly(address) ||
    isEmpty(address)
  ) {
    return { isValid: false, message: 'Invalid address input.' };
  }

  // Check for invalid leading/trailing or consecutive spaces
  if (hasInvalidSpaces(address)) {
    return { isValid: false, message: 'Address cannot have leading/trailing spaces or consecutive spaces.' };
  }

  // Address length validation based on dynamic min/max length
  if (address.length < minLength || address.length > maxLength) {
    return {
      isValid: false,
      message: `Address must be between ${minLength} and ${maxLength} characters long.`,
    };
  }

  // Ensure address contains at least one space (only if minLength > 1)
  if (minLength > 1 && !/\s/.test(address)) {
    return { isValid: false, message: 'Address must contain at least one space.' };
  }

  // Check for a single word greater than 40 characters
  const words = address.split(/\s+/); // Split by spaces to check words individually
  const longWords = words.filter(word => word.length > 40);
  if (longWords.length > 0) {
    return { isValid: false, message: `The word "${longWords[0]}" is too long (must be 40 characters or fewer).` };
  }

  // Regular expression to validate address (letters, numbers, spaces, and specific symbols)
  const addressRegex = /^[a-zA-Z0-9 ,/:()-]*$/;
  if (!addressRegex.test(address)) {
    const invalidChars = address.match(/[^a-zA-Z0-9 ,/:()-]/g);
    if (invalidChars) {
      return { isValid: false, message: `The characters "${invalidChars.join('", "')}" are not allowed.` };
    }
    return { isValid: false, message: 'Please enter a valid address (letters, numbers, spaces, and specific symbols only).' };
  }

  // Address is valid
  return { isValid: true, message: '' };
};

// Address filter function (to sanitize input by removing invalid characters)
export const filterAddress = (address) => {
  return address.replace(/[^@#/,\-_()&.A-Za-z0-9\s]/g, '');
};
